import React from "react";
import { SummaryCardIsEditableContext } from "./summary-card-editable-context";
import { SummaryCardOnSaveContext } from "./summary-card-onsave-context";
import "./summary-card.scss";

export interface SummaryCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** Descriptive text shown on the left side of the header row. */
  description?: string;
  /** Tag or badge rendered on the right side of the header row. */
  tag?: React.ReactNode;
  /** Body content — typically one or more LabeledValue components. */
  children?: React.ReactNode;
  /** Width of the card (CSS value or number in px). */
  width?: string | number;
  /** Custom border color for the card. */
  borderColor?: string;
  /** Additional CSS class for the root element. */
  className?: string;
  /**
   * When true, descendant `LabeledValue` components render editable inputs unless they
   * set their own `isEditable` prop.
   */
  isEditable?: boolean;
  /**
   * Optional save callback. This component does not render a save control—call `onSave`
   * from your own UI when edits should be persisted.
   */
  onSave?: () => void;
}

export const SummaryCard: React.FC<SummaryCardProps> = ({
  description,
  tag,
  children,
  width,
  borderColor,
  className = "",
  style,
  isEditable = false,
  onSave,
  ...rest
}) => {
  const cls = "summary-card";

  const rootClass = [cls, className].filter(Boolean).join(" ");

  const rootStyle: React.CSSProperties = {
    ...(width != null
      ? { width: typeof width === "number" ? `${width}px` : width }
      : {}),
    ...(borderColor ? { borderColor } : {}),
    ...style,
  };

  const hasStyle = Object.keys(rootStyle).length > 0;
  const hasHeader = description != null || tag != null;

  return (
    <SummaryCardIsEditableContext.Provider value={isEditable}>
      <SummaryCardOnSaveContext.Provider value={onSave}>
        <div
          className={rootClass}
          style={hasStyle ? rootStyle : undefined}
          {...rest}
        >
          {hasHeader && (
            <div className={`${cls}__header`}>
              {description != null && (
                <span className={`${cls}__description`}>{description}</span>
              )}
              {tag != null && (
                <div className={`${cls}__header-actions`}>
                  <span className={`${cls}__tag-slot`}>{tag}</span>
                </div>
              )}
            </div>
          )}
          {children != null && (
            <div className={`${cls}__body`}>{children}</div>
          )}
        </div>
      </SummaryCardOnSaveContext.Provider>
    </SummaryCardIsEditableContext.Provider>
  );
};

export {
  SummaryCardIsEditableContext,
  useSummaryCardIsEditable,
} from "./summary-card-editable-context";
export {
  SummaryCardOnSaveContext,
  useSummaryCardOnSave,
} from "./summary-card-onsave-context";

export default SummaryCard;
